import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Save, Settings, Package, LogOut } from "lucide-react";
import { useAdminAuth } from "@/hooks/useAdminAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const configSchema = z.object({
  public_key: z.string().min(10, "La clé publique est requise"),
  secret_key: z.string().min(10, "La clé secrète est requise"),
});

const AdminConfigPage = () => {
  const navigate = useNavigate();
  const { requireAuth, logout, getAuthHeaders } = useAdminAuth();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const { register, handleSubmit, formState: { errors }, setValue } = useForm({
    resolver: zodResolver(configSchema),
    defaultValues: {
      public_key: "",
      secret_key: ""
    }
  });

  useEffect(() => {
    if (!requireAuth()) return;

    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/admin/bkapay-config`, {
          headers: getAuthHeaders()
        });
        if (response.data?.public_key) {
          setValue("public_key", response.data.public_key);
        }
      } catch (error) {
        console.error("Erreur lors du chargement de la config", error);
        if (error.response?.status === 403) {
          toast.error("Session expirée. Veuillez vous reconnecter.");
          logout();
        }
      } finally {
        setFetching(false);
      }
    };

    fetchConfig();
  }, [setValue, requireAuth, logout, getAuthHeaders]);

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/bkapay-config`, values, {
        headers: getAuthHeaders()
      });
      toast.success("Configuration sauvegardée avec succès");
    } catch (error) {
      const message = error?.response?.data?.detail || "Erreur lors de la sauvegarde";
      toast.error(message);
      if (error.response?.status === 403) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              data-testid="back-button"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">Administration</h1>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate("/admin/book")}
              className="bg-blue-50 text-blue-600 hover:bg-blue-100"
            >
              📖 Contenu du Livre
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/admin/editor")}
            >
              <Settings className="mr-2 h-4 w-4" />
              Éditeur
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/admin/orders")}
              data-testid="view-orders-button"
            >
              <Package className="mr-2 h-4 w-4" />
              Commandes
            </Button>
            <Button
              variant="destructive"
              onClick={logout}
              size="sm"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Déconnexion
            </Button>
          </div>
        </div>

        <Card className="shadow-lg" data-testid="admin-config-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Settings className="h-6 w-6 text-primary" />
              <div>
                <CardTitle className="text-2xl">Configuration Bkapay</CardTitle>
                <CardDescription className="mt-1">
                  Configurez vos clés API Bkapay pour activer les paiements mobile money
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {fetching ? (
              <div className="text-center py-8 text-gray-500">Chargement...</div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="public_key">Clé publique Bkapay</Label>
                  <Input
                    id="public_key"
                    {...register("public_key")}
                    placeholder="pk_live_xxxxxxxxxx"
                    data-testid="admin-public-key"
                  />
                  <p className="text-sm text-gray-500">
                    Utilisée pour identifier votre compte marchand auprès de Bkapay
                  </p>
                  {errors.public_key && (
                    <p className="text-sm text-red-600">{errors.public_key.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="secret_key">Clé secrète Bkapay</Label>
                  <Input
                    id="secret_key"
                    type="password"
                    {...register("secret_key")}
                    placeholder="sk_live_xxxxxxxxxx"
                    data-testid="admin-secret-key"
                  />
                  <p className="text-sm text-gray-500">
                    Gardez cette clé secrète. Elle est utilisée pour signer les requêtes vers Bkapay.
                  </p>
                  {errors.secret_key && (
                    <p className="text-sm text-red-600">{errors.secret_key.message}</p>
                  )}
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Modes de paiement activés</h4>
                  <ul className="space-y-1 text-sm text-blue-800">
                    <li>• Orange Money</li>
                    <li>• M-Pesa (Vodacom)</li>
                    <li>• Airtel Money</li>
                  </ul>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-primary hover:bg-primary/90 text-white py-6 rounded-lg"
                  disabled={loading}
                  data-testid="save-keys-button"
                >
                  {loading ? (
                    "Sauvegarde en cours..."
                  ) : (
                    <>
                      <Save className="mr-2 h-5 w-5" />
                      Sauvegarder la configuration
                    </>
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminConfigPage;